import React, { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useLocation, useParams } from "react-router-dom";
import Loading from "../../../_components/Loading";
import RequestFailed from "../../../_components/RequestFailed";
import { Global } from "@emotion/react";
import {
  Avatar,
  Button,
  Divider,
  Heading,
  HStack,
  Link,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  useBreakpointValue,
  VStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from "@chakra-ui/react";
import { ChevronLeft, ChevronRight, Plus, Share } from "lucide-react";
import clsx from "clsx";
import { WorkHistory } from "../../../features/pro-portfolio/work-history";
import { Rating } from "../../../features/pro-portfolio/rating";
import { Helmet } from "react-helmet";
import { ProNotFound } from "features/pro-portfolio/pro-not-found";
import { FaFacebookF, FaTwitter } from "react-icons/fa";
import { logEvent } from "../../../helpers/analytics";
import { PortfolioBadgeSection } from "../../../features/pro-portfolio/badge-section";
import { PortfolioSectionHeading } from "../../../features/pro-portfolio/portfolio-section-heading";
import { CertificationCreator } from "../../../features/pro-portfolio/certification-creator";
import { Certifications } from "../../../features/pro-portfolio/certifications";
import HelmetIcon from "../../../assets/helmet-icon.png";
import Slider from "react-slick";
import { EducationEntries } from "features/pro-portfolio/education-entries";
import PortfolioImageContainer, {
  PortfolioImage,
} from "features/pro-portfolio/portfolio-image";
import { GET_PORTFOLIO_WITHOUT_MEDIA } from "features/pro-portfolio/queries";
import { EmploymentHistory } from "features/pro-portfolio/employment-history";
import { cloneDeep } from "@apollo/client/utilities";

export default function ProProfilePage() {
  const { handle } =
    useParams<{
      handle: string;
    }>();

  const { pathname } = useLocation();

  const url = `${window.location.origin}${pathname}`;

  const [copied, setCopied] = React.useState(false);

  const [showShareModal, setShowShareModal] = React.useState(false);

  const [showCertificationCreateModal, setShowCertificationCreateModal] =
    React.useState(false);

  const [currentSlide, setCurrentSlide] = React.useState(0);

  // Custom arrow components
  const CustomPrevArrow = (props: any) => {
    const { onClick } = props;
    return (
      <div
        className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 cursor-pointer bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-shadow"
        onClick={onClick}
      >
        <ChevronLeft className="w-6 h-6 text-gray-700" />
      </div>
    );
  };

  const CustomNextArrow = (props: any) => {
    const { onClick } = props;
    return (
      <div
        className="absolute right-1 top-1/2 transform -translate-y-1/2 z-10 cursor-pointer bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-shadow"
        onClick={onClick}
      >
        <ChevronRight className="w-6 h-6 text-gray-700" />
      </div>
    );
  };

  const carouselSettings = useBreakpointValue({
    base: {
      slidesToShow: 1,
      prevArrow: <CustomPrevArrow />,
      nextArrow: <CustomNextArrow />,
    },
    lg: {
      prevArrow: <CustomPrevArrow />,
      nextArrow: <CustomNextArrow />,
    },
  });

  React.useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  }, [copied]);

  // Query for portfolio without media so the page can load faster while the media is loading
  const {
    data: portfolioDataWithoutMedia,
    loading: loadingPortfolioWithoutMedia,
    error: portfolioWithoutMediaError,
  } = useQuery(GET_PORTFOLIO_WITHOUT_MEDIA, {
    variables: { handle },
  });

  const projectHistoryMedia = useMemo(() => {
    const portfolio = portfolioDataWithoutMedia?.proPortfolio;

    if (!portfolio) return [];

    const cloned = cloneDeep(portfolio.workHistory);

    const items: PortfolioImage[] = [];

    cloned.forEach((item) => {
      item.images.forEach((image) => {
        if (!image.originalUrl || !image.thumbnailUrl) return;

        items.push({
          name: item.name,
          type: image.mimeType,
          urls: {
            originalUrl: image.originalUrl,
            thumbnailUrl: image.thumbnailUrl,
          },
        });
      });
    });

    return items;
  }, [portfolioDataWithoutMedia?.proPortfolio]);

  const employmentHistoryMedia = useMemo(() => {
    const portfolio = portfolioDataWithoutMedia?.proPortfolio;

    if (!portfolio) return [];

    const cloned = cloneDeep(portfolio.employmentHistory);

    const items: PortfolioImage[] = [];

    cloned.forEach((item) => {
      item.images.forEach((image) => {
        if (!image.originalUrl || !image.thumbnailUrl) return;

        items.push({
          name: item.jobTitle,
          type: image.mimeType,
          urls: {
            originalUrl: image.originalUrl,
            thumbnailUrl: image.thumbnailUrl,
          },
        });
      });
    });

    return items;
  }, [portfolioDataWithoutMedia?.proPortfolio]);

  if (loadingPortfolioWithoutMedia) return <Loading />;
  if (portfolioWithoutMediaError) return <RequestFailed />;

  if (!portfolioDataWithoutMedia) return <RequestFailed />;

  if (!portfolioDataWithoutMedia.proPortfolio) return <ProNotFound />;

  const portfolio = portfolioDataWithoutMedia.proPortfolio;

  const portfolioMedia = [...projectHistoryMedia, ...employmentHistoryMedia];

  const canEdit = false; // we no longer have edit mode in this repository and cleanups will be done soon.

  const noOfJobsCompleted = portfolio.workHistory.length;
  const noOfEmploymentHistoryItems = portfolio.employmentHistory.length;

  const shortenedName = `${portfolio.firstName} ${portfolio.lastName[0]}.`;

  const handleShare = () => {
    logEvent("pro_portfolio/share_profile_clicked");

    if (!portfolio) return;

    /**
     * Open the Web Share API
     */
    if (navigator.share) {
      navigator
        .share({
          title: `${shortenedName} | LaborHack Pro`,
          text: `Check out ${shortenedName}'s profile on LaborHack`,
          url,
        })
        .then(() => {
          logEvent("pro_portfolio/profile_shared");
        })
        .catch((error) => {
          if (error.name === "AbortError") {
            return;
          }

          logEvent("pro_portfolio/profile_share_failed", {
            error: error.message,
          });

          setShowShareModal(true);
        });
    } else {
      setShowShareModal(true);
    }
  };

  let experience: string | undefined;

  if (portfolio.experience && portfolio.experience > 0) {
    experience =
      portfolio.experience > 1
        ? `${portfolio.experience} years of experience`
        : `${portfolio.experience} year of experience`;
  } else if (portfolio.experience === 0) {
    experience = "< 1 year of experience";
  }

  return (
    <>
      <Helmet>
        <title>
          {portfolio.firstName} {portfolio.lastName[0]}. | LaborHack
        </title>
      </Helmet>
      <Global
        styles={`
       body {
          background-color: white !important;
        }
      `}
      />

      <Modal
        isOpen={showCertificationCreateModal}
        onClose={() => setShowCertificationCreateModal(false)}
        isCentered
        closeOnOverlayClick
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader className="font-[AeonikPro] !text-2xl">
            Add Certification
          </ModalHeader>
          <Divider />
          <VStack py={4} px={6} alignItems="start">
            <CertificationCreator
              onClose={() => setShowCertificationCreateModal(false)}
            />
          </VStack>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        isCentered
        closeOnOverlayClick
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Share Profile</ModalHeader>
          <VStack pb={8} px={6} alignItems="start">
            <HStack>
              <Link
                href={`https://twitter.com/intent/tweet?text=Check out ${portfolio.firstName} ${portfolio.lastName[0]} on LaborHack&url=${url}`}
                isExternal
              >
                <Button
                  rightIcon={<FaTwitter />}
                  colorScheme="twitter"
                  variant="solid"
                  onClick={() =>
                    logEvent("pro_portfolio/twitter_share_clicked")
                  }
                >
                  Tweet
                </Button>
              </Link>
              <Link
                href={
                  "https://www.facebook.com/sharer/sharer.php?u=" +
                  encodeURIComponent(url)
                }
                isExternal
              >
                <Button
                  rightIcon={<FaFacebookF />}
                  colorScheme="facebook"
                  variant="solid"
                  onClick={() =>
                    logEvent("pro_portfolio/facebook_share_clicked")
                  }
                >
                  Post to Facebook
                </Button>
              </Link>
            </HStack>
          </VStack>
        </ModalContent>
      </Modal>
      <HStack w="full" bgColor="white" justifyContent="center">
        <Stack
          px={{
            base: 0,
            lg: 5,
          }}
          py={{
            base: 8,
            lg: 20,
          }}
          spacing={{
            base: 12,
            lg: 0,
          }}
          direction={{
            base: "column",
            lg: "row",
          }}
          w="full"
          maxW="container.xl"
          alignItems={{
            base: "start",
            lg: "center",
          }}
        >
          <div className="flex flex-col w-full border-flatteredFlamingo-50 lg:border-[16px]">
            <div className="flex flex-col lg:flex-row lg:justify-between gap-8 border px-6 py-9">
              <div className="flex items-start gap-5">
                <Avatar
                  bg="flatteredFlamingo.500"
                  textColor={"#FEFEFE"}
                  size={{
                    base: "xl",
                    lg: "2xl",
                  }}
                  name={`${portfolio.firstName} ${portfolio.lastName[0]}.`}
                  src={""}
                />
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1">
                    <Heading
                      as="h2"
                      fontSize={{
                        base: 24,
                        lg: 28,
                      }}
                      fontWeight={500}
                      textColor={"#040A0D"}
                    >
                      {portfolio.firstName} {portfolio.lastName[0]}.
                    </Heading>
                    <p className="font-normal text-xl text-[#464646]">
                      {portfolio.title}
                    </p>
                    {experience && (
                      <p className="font-normal text-base lg:text-lg text-gray-600">
                        {experience}
                      </p>
                    )}
                  </div>
                  {portfolio.averageRating > 0 && (
                    <div className="flex items-center gap-2">
                      <Rating rating={portfolio.averageRating} />
                      <p className="text-sm">
                        {portfolio.averageRating &&
                          portfolio.averageRating.toFixed(2)}{" "}
                        <span className="text-gray-500">
                          ({portfolio.ratingCount} reviews)
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col lg:flex-row gap-6 w-full lg:w-auto">
                <Button
                  leftIcon={<Share className="w-5 h-5" />}
                  colorScheme="prussianBlue"
                  size="lg"
                  variant="ghost"
                  className={clsx("w-full order-2 lg:order-1 lg:w-auto")}
                  onClick={handleShare}
                >
                  Share Profile
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 border-l border-r border-b">
              <div className="col-span-1 flex flex-col">
                <div className="p-6 flex flex-col gap-6 border-b items-start">
                  <PortfolioSectionHeading>
                    Contact Information
                  </PortfolioSectionHeading>
                  <div className="flex flex-col gap-4">
                    {!portfolio.isEmailHidden && (
                      <div className="flex flex-col gap-2">
                        <p className="text-base text-[#6E6E6E] font-[Inter]">
                          Email address
                        </p>
                        <p className="text-lg font-[Inter]">
                          {portfolio.email}
                        </p>
                      </div>
                    )}
                    <div className="flex flex-col gap-2">
                      <p className="text-base text-[#6E6E6E] font-[Inter]">
                        Phone number
                      </p>
                      <p className="text-lg font-[Inter]">{portfolio.phone}</p>
                    </div>
                  </div>
                </div>
                {portfolio.averageRating > 0 && (
                  <div className="p-6 grid grid-cols-2 gap-5 border-b items-start">
                    <div>
                      <p className="font-[Inter] text-lg text-[#121212]">
                        Punctuality
                      </p>
                    </div>
                    <Rating
                      rating={
                        portfolio.averageLaborHackJobsRatings
                          .averagePunctualityRating
                      }
                    />
                    <div>
                      <p className="font-[Inter] text-lg text-[#121212]">
                        Appearance
                      </p>
                    </div>
                    <Rating
                      rating={
                        portfolio.averageLaborHackJobsRatings
                          .averageAppearanceRating
                      }
                    />
                    <div>
                      <p className="font-[Inter] text-lg text-[#121212]">
                        Behaviour
                      </p>
                    </div>
                    <Rating
                      rating={
                        portfolio.averageLaborHackJobsRatings
                          .averageBehaviorRating
                      }
                    />
                    <div>
                      <p className="font-[Inter] text-lg text-[#121212]">
                        Proper tool usage
                      </p>
                    </div>
                    <Rating
                      rating={
                        portfolio.averageLaborHackJobsRatings
                          .averageProperToolUsageRating
                      }
                    />
                    <div>
                      <p className="font-[Inter] text-lg text-[#121212]">
                        Post service clean up
                      </p>
                    </div>
                    <Rating
                      rating={
                        portfolio.averageLaborHackJobsRatings
                          .averagePostServiceCleanUpRating
                      }
                    />
                  </div>
                )}
                <div className="p-6 flex gap-5 border-b items-start">
                  <div className="w-[64px] h-[64px]">
                    <img src={HelmetIcon} alt="Helmet Icon" />
                  </div>
                  <div className="flex flex-col gap-3">
                    <Heading
                      as="h3"
                      fontSize={{
                        base: "22px",
                        lg: "26px",
                      }}
                      textColor={"#040A0D"}
                    >
                      {noOfEmploymentHistoryItems > 10
                        ? `10+`
                        : noOfEmploymentHistoryItems}
                    </Heading>
                    <p className="text-base lg:text-lg font-normal font-[Inter] text-[#464646]">
                      {noOfEmploymentHistoryItems === 1
                        ? "Position held"
                        : "Positions held"}
                    </p>
                  </div>
                </div>
                <div className="p-6 flex gap-5 border-b items-start">
                  <div className="w-[64px] h-[64px]">
                    <img src={HelmetIcon} alt="Helmet Icon" />
                  </div>
                  <div className="flex flex-col gap-3">
                    <Heading
                      as="h3"
                      fontSize={{
                        base: "22px",
                        lg: "26px",
                      }}
                      textColor={"#040A0D"}
                    >
                      {noOfJobsCompleted > 100 ? `100+` : noOfJobsCompleted}
                    </Heading>
                    <p className="text-base lg:text-lg font-normal font-[Inter] text-[#464646]">
                      {noOfJobsCompleted === 1
                        ? "Project completed"
                        : "Projects completed"}
                    </p>
                  </div>
                </div>

                <PortfolioBadgeSection
                  portfolio={portfolio}
                  editMode={canEdit}
                />
              </div>
              <div className="col-span-1 lg:col-span-2 border-l flex flex-col">
                <div className="p-6 flex flex-col gap-4 border-b items-start">
                  <PortfolioSectionHeading>Profile</PortfolioSectionHeading>

                  <article className="flex flex-col gap-4 font-[Inter]">
                    {portfolio.overview ? (
                      portfolio.overview
                        .split("\n\n")
                        .map((para, index) => <p key={index}>{para}</p>)
                    ) : (
                      <p className="text-gray-500 font-[Inter]">
                        No overview provided
                      </p>
                    )}
                  </article>
                </div>

                {portfolioMedia.length > 0 && (
                  <div className="border-b">
                    <div
                      className={clsx("p-6 flex items-start w-full", {
                        "justify-between": canEdit,
                      })}
                    >
                      <div>
                        <PortfolioSectionHeading>
                          Gallery
                        </PortfolioSectionHeading>
                        {portfolioMedia.length > 0 && (
                          <p className="text-[#6B7280] text-sm font-normal">
                            Tap on an image to view it in full screen.
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Desktop view gallery */}
                    <div
                      className={`w-full px-6 hidden lg:block ${
                        portfolioMedia.length < 1 ? "py-4" : "py-8"
                      }`}
                    >
                      {portfolioMedia.length >= 3 ? (
                        <div className="relative">
                          <Slider
                            dots={false}
                            infinite={true}
                            speed={500}
                            arrows={true}
                            slidesToShow={3}
                            slidesToScroll={1}
                            autoplay={true}
                            autoplaySpeed={3000}
                            pauseOnHover={false}
                            beforeChange={(oldIndex, newIndex) =>
                              setCurrentSlide(newIndex)
                            }
                            {...carouselSettings}
                          >
                            {portfolioMedia.map((image, index) => (
                              <PortfolioImageContainer
                                key={index}
                                image={image}
                              />
                            ))}
                          </Slider>
                          <div className="flex justify-center mt-4">
                            <div className="bg-black bg-opacity-70 text-white px-3 py-1 rounded-full text-sm">
                              {currentSlide + 1} / {portfolioMedia.length}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 gap-4">
                          {portfolioMedia.map((image, index) => (
                            <PortfolioImageContainer
                              key={index}
                              image={image}
                            />
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Mobile view gallery */}
                    <div
                      className={`w-full px-6 block lg:hidden ${
                        portfolioMedia.length < 1 ? "py-4" : "py-8"
                      }`}
                    >
                      {portfolioMedia.length >= 2 ? (
                        <div className="w-full">
                          <Slider
                            dots={false}
                            infinite={true}
                            speed={500}
                            arrows={true}
                            slidesToShow={2}
                            slidesToScroll={1}
                            autoplay={true}
                            autoplaySpeed={3000}
                            pauseOnHover={false}
                            beforeChange={(oldIndex, newIndex) =>
                              setCurrentSlide(newIndex)
                            }
                            {...carouselSettings}
                          >
                            {portfolioMedia.map((image, index) => (
                              <PortfolioImageContainer
                                key={index}
                                image={image}
                              />
                            ))}
                          </Slider>
                          <div className="flex justify-center mt-4">
                            <div className="bg-black bg-opacity-70 text-white px-3 py-1 rounded-full text-sm">
                              {currentSlide + 1} / {portfolioMedia.length}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-4">
                          {portfolioMedia.map((image, index) => (
                            <PortfolioImageContainer
                              key={index}
                              image={image}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!!portfolio.certifications.length && (
                  <div className="p-6 flex flex-col items-start gap-4 border-b ">
                    <div
                      className={clsx("flex items-center w-full", {
                        "justify-between": canEdit,
                      })}
                    >
                      <PortfolioSectionHeading>
                        Certifications
                      </PortfolioSectionHeading>

                      {canEdit && (
                        <div className="flex items-center">
                          <Button
                            variant="outline"
                            leftIcon={<Plus className="w-6 h-6" />}
                            colorScheme="flatteredFlamingo"
                            onClick={() =>
                              setShowCertificationCreateModal(true)
                            }
                          >
                            Add
                          </Button>
                        </div>
                      )}
                    </div>

                    {portfolio.certifications.length ? (
                      <Certifications
                        items={portfolio.certifications}
                        editable={canEdit}
                      />
                    ) : (
                      <p className="text-gray-500">
                        No certifications provided
                      </p>
                    )}
                  </div>
                )}
                {!!portfolio.educationEntries.length && (
                  <div className="p-6 flex flex-col gap-4 border-b items-start">
                    <PortfolioSectionHeading>Education</PortfolioSectionHeading>
                    <EducationEntries
                      items={portfolio.educationEntries}
                      editable={canEdit}
                    />
                  </div>
                )}
                <div className="p-6 flex flex-col gap-4 border-b items-start">
                  <div
                    className={clsx("flex items-center w-full", {
                      "justify-between": canEdit,
                    })}
                  >
                    <PortfolioSectionHeading>
                      Work History
                    </PortfolioSectionHeading>
                  </div>

                  <Tabs
                    variant="line"
                    colorScheme="flatteredFlamingo"
                    className="w-full"
                  >
                    <TabList borderColor={"#C9C9C9"}>
                      <Tab
                        fontSize={{
                          base: "14px",
                          lg: "16px",
                        }}
                        fontFamily={"AeonikPro"}
                      >
                        Employment history ({noOfEmploymentHistoryItems})
                      </Tab>
                      <Tab
                        fontSize={{
                          base: "14px",
                          lg: "16px",
                        }}
                        fontFamily={"AeonikPro"}
                      >
                        Project history ({noOfJobsCompleted})
                      </Tab>
                    </TabList>
                    <TabPanels>
                      <TabPanel p={0}>
                        <EmploymentHistory
                          items={portfolio.employmentHistory || []}
                          editable={canEdit}
                        />
                      </TabPanel>
                      <TabPanel p={0}>
                        <WorkHistory
                          items={portfolio.workHistory || []}
                          editable={canEdit}
                        />
                      </TabPanel>
                    </TabPanels>
                  </Tabs>
                </div>
              </div>
            </div>
          </div>
        </Stack>
      </HStack>
    </>
  );
}
